"use client";

import { constructionStatus } from "@/info/unit/constructionStatus";
import * as Slider from "@radix-ui/react-slider";
import { useState } from "react";

interface PossessionSectionProps {
	value: string;
	onChange: (value: string) => void;
	showShortLabel?: boolean;
}

// Get the Under Construction options for possession
const possessionScale = constructionStatus.find(status => status.value === "underConstruction")?.options || [];

export function PossessionSection({ value, onChange, showShortLabel }: PossessionSectionProps) {
	const [showTooltip, setShowTooltip] = useState(false);

	// Find the closest index for the current value
	const findClosestIndex = (val: string) => {
		if (!val) return 0; // Default to first option when empty
		const index = possessionScale.findIndex(item => item.value === val);
		return index >= 0 ? index : 0;
	};

	const currentSliderIndex = findClosestIndex(value || "");

	const handleSliderChange = (newIndices: [number]) => {
		const newIndex = newIndices[0];
		const newValue = possessionScale[newIndex]?.value;
		if (newValue) {
			onChange(newValue);
		}
	};

	// Format value for tooltip display
	const formatValueLabel = (index: number) => {
		const item = possessionScale[index];
		return item ? item.label : "";
	};

	return (
		<div className="space-y-6">
			{/* Slider with Tooltip and Tick Marks */}
			<div className="relative pt-8">
				<div className="relative h-5">
					{/* Tooltip */}
					{showTooltip && (
						<div
							className="absolute -top-12 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10 pointer-events-none"
							style={{
								left: `${(currentSliderIndex / (possessionScale.length - 1)) * 100}%`,
								transform: "translateX(-50%)",
							}}
						>
							{formatValueLabel(currentSliderIndex)}
						</div>
					)}

					<Slider.Root
						className="relative flex items-center select-none touch-none w-full h-5"
						value={[currentSliderIndex]}
						onValueChange={handleSliderChange}
						onValueCommit={() => setShowTooltip(false)}
						min={0}
						max={possessionScale.length - 1}
						step={1}
					>
						<Slider.Track className="bg-gray-300 relative grow rounded-full h-1">
							<Slider.Range className="absolute bg-gray-500 rounded-full h-full" />
						</Slider.Track>
						<Slider.Thumb
							aria-label="Possession Timeline"
							className="block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
							onMouseDown={() => setShowTooltip(true)}
							onTouchStart={() => setShowTooltip(true)}
						/>
					</Slider.Root>
				</div>

				{/* Tick marks and labels */}
				<div className="relative mt-4 pb-4">
					{possessionScale.map((option, index) => {
						const isSelected = index === currentSliderIndex;
						const isFirst = index === 0;
						const isLast = index === possessionScale.length - 1;
						const leftPosition = (index / (possessionScale.length - 1)) * 100;

						return (
							<div
								key={option.value}
								className="absolute flex flex-col items-center"
								style={{
									left: `${leftPosition}%`,
									transform: isFirst ? "translateX(0%)" : isLast ? "translateX(-100%)" : "translateX(-50%)",
								}}
							>
								{/* Tick mark */}
								<div className={`w-px h-2 ${isSelected ? "bg-primary-600" : "bg-gray-400"}`} />
								{/* Label */}
								<span
									className={`text-xs font-semibold ${isSelected ? "text-primary-600" : "text-gray-600"} whitespace-nowrap`}
								>
									{showShortLabel ? option.shortLabel : option.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
